/**
 * Wrapper de `window.Telegram.WebApp` — el SDK se carga vía `<script>` en `index.html`
 * (`https://telegram.org/js/telegram-web-app.js`), nunca como paquete npm (Telegram lo sirve
 * así a propósito, para que siempre sea la versión vigente). Todo lo demás del proyecto pasa
 * por aquí, nunca accede a `window.Telegram` directamente.
 */

/** Enlace público al bot -- mismo valor que `landing/src/lib/constants.ts::TELEGRAM_BOT_URL`
 * (un username de bot de Telegram es información pública, no un secreto). Usado por
 * `LoginScreen.tsx` (P0.54) como alternativa a "Entrar con Telegram" cuando alguien abre esta
 * URL fuera de un cliente de Telegram. */
export const TELEGRAM_BOT_URL = 'https://t.me/Jhon_sportbot'

/** Username sin "@" ni dominio -- lo exige el atributo `data-telegram-login` del Telegram
 * Login Widget (P0.54, https://core.telegram.org/widgets/login). Mismo bot que `TELEGRAM_BOT_URL`. */
export const TELEGRAM_BOT_USERNAME = 'Jhon_sportbot'

interface TelegramThemeParams {
  bg_color?: string
  secondary_bg_color?: string
  section_bg_color?: string
  text_color?: string
  hint_color?: string
  subtitle_text_color?: string
  link_color?: string
  button_color?: string
  button_text_color?: string
  destructive_text_color?: string
}

interface TelegramWebApp {
  initData: string
  initDataUnsafe: Record<string, unknown>
  themeParams: TelegramThemeParams
  colorScheme: 'light' | 'dark'
  ready: () => void
  expand: () => void
  close: () => void
  BackButton: {
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  MainButton: {
    text: string
    show: () => void
    hide: () => void
    setText: (text: string) => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  onEvent: (eventType: string, cb: () => void) => void
  offEvent: (eventType: string, cb: () => void) => void
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp }
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null
}

/** `true` cuando corre de verdad dentro de un cliente de Telegram (no un navegador suelto). */
export function isInsideTelegram(): boolean {
  const app = getTelegramWebApp()
  return Boolean(app && app.initData)
}

export function getInitData(): string | null {
  return getTelegramWebApp()?.initData || null
}

/** Abre una URL externa (ej. Stripe Checkout) -- dentro de Telegram real usa `openLink` (el
 * cliente de Telegram gestiona la navegación/vuelta a la app); fuera de Telegram, navega
 * directo en la misma pestaña. Nunca `window.open` a ciegas -- Telegram Desktop/iOS bloquean
 * popups no iniciados por su propio SDK. */
export function openExternalLink(url: string): void {
  const app = getTelegramWebApp()
  if (app && isInsideTelegram()) {
    app.openLink(url)
  } else {
    window.location.href = url
  }
}

function hexToRgbTriplet(hex: string | undefined): string | null {
  if (!hex) return null
  const clean = hex.replace('#', '')
  if (clean.length !== 6) return null
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  if ([r, g, b].some((n) => Number.isNaN(n))) return null
  return `${r} ${g} ${b}`
}

/**
 * INFRAESTRUCTURA PRESERVADA (no invocada desde este módulo) -- mapea
 * `Telegram.WebApp.themeParams` a las variables CSS `--jd-*`. Reutilizable si en el futuro
 * se ofrece "adaptar al tema nativo de Telegram" como opción EXPLÍCITA del usuario; `export`
 * a propósito (nunca código muerto sin referencia) aunque hoy no la llame nadie fuera de
 * este archivo -- ver docstring de `applyTelegramTheme` (Home Premium + DARK canónico,
 * 01/10/2026) para la causa raíz real que motiva dejar de invocarla por defecto.
 */
export function mapTelegramThemeToCssVars(app: TelegramWebApp): void {
  const root = document.documentElement
  root.setAttribute('data-theme', app.colorScheme === 'light' ? 'light' : 'dark')

  const mapping: Array<[keyof TelegramThemeParams, string]> = [
    ['bg_color', '--jd-bg'],
    ['secondary_bg_color', '--jd-panel'],
    ['section_bg_color', '--jd-panel-elevated'],
    ['text_color', '--jd-text'],
    ['hint_color', '--jd-text-muted'],
    ['subtitle_text_color', '--jd-text-dim'],
    ['button_color', '--jd-accent'],
  ]
  for (const [themeKey, cssVar] of mapping) {
    const rgb = hexToRgbTriplet(app.themeParams[themeKey])
    if (rgb) root.style.setProperty(cssVar, rgb)
  }
}

/**
 * Home Premium + DARK canonico (01/10/2026, Sección 1 del encargo): JA ES DARK -- identidad
 * de marca fija del producto cliente (fondo negro, verde John Anthony), NUNCA derivada del
 * `colorScheme`/`themeParams` del cliente Telegram de cada usuario. Causa raíz real
 * encontrada: esta función, en su versión anterior, sobreescribía `--jd-bg`/`--jd-text`/
 * `--jd-accent`/etc. vía `element.style.setProperty` (mayor especificidad que la hoja de
 * estilos) usando los colores reales de `Telegram.WebApp.themeParams` -- un usuario con
 * Telegram en modo claro (o con cualquier tema oscuro no-JA) veía la app romper su
 * identidad visual canónica, exactamente lo que este sprint prohíbe. Se neutraliza la
 * aplicación real (nunca se borra la infraestructura: `mapTelegramThemeToCssVars` arriba
 * conserva la lógica intacta, reutilizable sin resucitar código borrado).
 */
export function applyTelegramTheme(): void {
  // No-op deliberado -- ver docstring de arriba. `mapTelegramThemeToCssVars` queda
  // preservada y exportada para un futuro modo opt-in, nunca invocada por defecto.
}

export function initTelegramWebApp(): void {
  const app = getTelegramWebApp()
  if (!app) return
  app.ready()
  app.expand()
  applyTelegramTheme()
  app.onEvent('themeChanged', applyTelegramTheme)
}
